import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ArrowRight, Mail, ChevronDown, Instagram, CheckCircle, Clock, TrendingUp, User, Code } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

// --- Data for Navigation/Footer Consistency ---
const services = [
  {
    id: 'dev',
    title: 'Website Design and Development',
    link: '/web-development',
  },
  {
    id: 'revamp',
    title: 'Website Revamp',
    link: '/website-revamp',
  },
  {
    id: 'hosting',
    title: 'Server Hosting & Maintenance',
    link: '/hosting-maintenance',
  },
];

// --- Case Study Specific Data ---
const inosinProject = {
  title: "Innosin Labs - Digital Transformation",
  tagline: "Bringing 40 Years of Laboratory Excellence Online.",
  client: "Innosin Labs",
  service: " Website Design, Development & Ongoing Maintenance",
  duration: "6 Weeks Build Time",
  technologies: ["React", "Tailwind CSS", "SEO Strategy", "Local Business Focus"],
  heroImage: "/inosin.png", // Main image for the banner
  
  
  showcase: {
    mainPageImages: [
      
      { id: 1, src: "/inosin-maingif.webm", isAnimated: true, caption: "Premium First Impressions: A clean, animation of the company logo to welcome users to the premium Innosin Experiene.", title:
        "Loading Screen to Main Page",
       },
      { id: 2, src: "/inosin-maingif-1.webm", isAnimated: true, caption: "Seamless Scrolling Experience: Fully responsive architecture ensuring flawless presentation and navigation on any device, ensuring a smooth user experience.",title: "Animated & Responsive Main Page" },
      { 
        id: 3, 
        src: ["/inosin-main-3.png", "/inosin-main-4.png"], 
        isSlider: true, 
        caption: "High Fidelity Visuals: Images and Videos that capture your brand image and alligned to your marketing vision.", 
        title: "Media Embeddings"
      },
    ],
    catalogueImages: [
      { id: 1, src: "/inosin-catalogue-1.png", caption: "Organized Product Catalogues: Intuitive filtering and search functions allow for viewing of hundreds of products" },
      { id: 2, src: "/inosin-catalogue-2.png", caption: "Product Specification View: Detailed product descriptions, photos and models for clients to understand the product." },
    ],
    featureImage: { src: "/inosin-contact-form.png", caption: "High-Conversion Lead Forms & Userbase: Secure, custom-built contact forms and user accounts to manage customer relations." },
  },
  
  
  results: {
    heading: "The Results: Enhanced Credibility and Lead Flow",
    points: [
      { icon: <TrendingUp className="w-6 h-6 text-cyan-500" />, text: "Leads Increased: 40% rise in qualified B2B inquiries within the first two months post-launch." },
      { icon: <Clock className="w-6 h-6 text-cyan-500" />, text: "Page Speed Score: Achieved an 'Excellent' rating, ensuring minimal bounce rate and a positive user experience." },
      { icon: <CheckCircle className="w-6 h-6 text-cyan-500" />, text: "Online Authority Established: Successfully transitioned a legacy business into the digital realm." },
    ],
  },
};


const AnimatedSection = ({ children, animationClass = 'animate-slideUp', delay = '0ms', className = '' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} ${isVisible ? animationClass : 'opacity-0'}`}
      style={{ animationDelay: isVisible ? delay : '0ms' }}
    >
      {children}
    </div>
  );
};


const TwoImageSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-slide effect every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  // Only render if there are exactly 2 images
  if (images.length !== 2) return null;

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div 
        className="flex transition-transform duration-700 ease-in-out"
        // Container width is 200% to hold both 50% images side-by-side
        // Transform shifts by 0% (index 0) or -50% (index 1)
        style={{ width: '200%', transform: `translateX(-${currentIndex * 50}%)` }}
      >
        <img
          src={images[0]}
          alt="Showcase Slide 1"
          className="w-1/2 h-auto object-cover flex-shrink-0"
        />
        <img
          src={images[1]}
          alt="Showcase Slide 2"
          className="w-1/2 h-auto object-cover flex-shrink-0"
        />
      </div>
    </div>
  );
};
// -----------------------------------------------------------



const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-12 border-b border-slate-700 pb-12">
                    {/* Column 1: Brand & Contact */}
                    <div className="col-span-2 md:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        
                        <div className="flex space-x-4 pt-2">
                        <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                        <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                            
                        </div>
                    </div>

                    {/* Column 2: Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                            
                        </ul>
                    </div>

                    {/* Column 3: Services */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Column 4: Get In Touch */}
                    <div className="space-y-4 col-span-2 md:col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors">vyxionlabs.info@gmail.com</a>
                            </li>
                        </ul>
                    </div>
                </div>
                
                {/* --- LEGAL LINKS --- */}
                <div className="flex justify-center md:justify-start space-x-6 mt-8">
                    
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>
                {/* ------------------- */}

                {/* Copyright */}
                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};
// ---------------------------------------------------------------------------------

// --- Main Component ---
export default function InosinCaseStudy() {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const servicesDropdownRef = useRef(null);

  const handleServicesClick = (e) => {
    e.stopPropagation();
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [servicesDropdownRef]);

  const handleMenuClick = () => {
    setIsContactPopupOpen(!isContactPopupOpen);
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 text-slate-900 min-h-screen antialiased relative overflow-x-hidden">
      
      {/* Shared Styles */}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float { 
          0%, 100% { transform: translateY(0px) rotate(0deg); } 
          50% { transform: translateY(-20px) rotate(5deg); } 
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        .animate-slideUp { animation: slideUp 0.6s ease-out forwards; }
        .float-animation { animation: float 6s ease-in-out infinite; }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
       

        /* Menu Modal/Overlay CSS */
        .modal-overlay {
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(8px);
            z-index: 9999; opacity: 0; visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .modal-overlay.open { opacity: 1; visibility: visible; }
        .modal-content {
            position: fixed; top: 0; right: 0; width: 90%; max-width: 400px;
            height: 100%; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(30px);
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2); transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
            overflow-y: auto; padding: 3rem 1.5rem;
        }
        .modal-overlay.open .modal-content { transform: translateX(0); }
      `}</style>
      
      {/* Animated Background */}
      

      {/* Header (Reused) - MODIFIED: Removed backdrop-blur-md and bg-white/80 */}
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox Layout */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                   <div className="flex flex-col p-2">
                       <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Design & Development
                       </a>
                       <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Revamp
                       </a>
                       <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Server Hosting & Maintenance
                       </a>
                   </div>
               </div>
               
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">About Us</a>
                <a href="/ourworks" className="text-neutral-900 font-bold cursor-pointer">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-900 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Contact Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft ">
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft " onClick={handleMenuClick}>Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft " onClick={handleMenuClick}>Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft " onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft " onClick={handleMenuClick}>About Us</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
            <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
           
        </div>
      </div>

      <main className="relative z-10">
        
        {/* --- 1. HERO / OVERVIEW SECTION --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <AnimatedSection className="text-center space-y-6 max-w-4xl mx-auto">
            
            <h1 className="text-5xl lg:text-7xl font-black text-slate-900 leading-tight">
              {inosinProject.title}
            </h1>
            <p className="text-xl text-slate-700 font-medium">
              {inosinProject.tagline}
            </p>
            <div className="flex justify-center items-center gap-6 pt-4 text-slate-600 flex-wrap">
              <span className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-cyan-500" />
                Client: {inosinProject.client}
              </span>
              <span className="flex items-center gap-2 text-sm">
                <Code className="w-4 h-4 text-cyan-500" />
                Service: {inosinProject.service}
              </span>
              <span className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-cyan-500" />
                Duration: {inosinProject.duration}
              </span>
            </div>
          </AnimatedSection>

          {/* Project Hero Image */}
          <AnimatedSection  className="mt-16 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/50">
            <img 
                src={inosinProject.heroImage} 
                alt={inosinProject.title} 
                className="w-full h-auto object-cover"
            />
          </AnimatedSection>
        </section>
        
        {/* --- 2. MAIN PAGE SHOWCASE (3 Images/Videos) --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <AnimatedSection className="text-center mb-16">
                <h2 className="text-4xl font-extrabold text-slate-900">
                    The Aesthetics: Capturing Excellence
                </h2>
                <p className="text-lg text-slate-600 mt-2">
                    We crafted a digital facade that instantly communicates 40 years of dedication and expertise.
                </p>
            </AnimatedSection>

            <div className="space-y-20">
                {inosinProject.showcase.mainPageImages.map((item, index) => (
                    <AnimatedSection key={item.id}  className={`grid lg:grid-cols-12 gap-10 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                        
                        {/* Image / Video Block (UPDATED LOGIC) */}
                        <div className={`lg:col-span-7 rounded-2xl overflow-hidden shadow-2xl border border-white/50 ${index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}`}>
                            {item.isSlider ? (
                                <TwoImageSlider images={item.src} />
                            ) : item.isAnimated ? (
                                // Use <video> tag for optimized webm/mp4 animations
                                <video 
                                    key={item.src} 
                                    autoPlay 
                                    loop 
                                    muted 
                                    playsInline 
                                    className="w-full h-auto object-cover transition-transform duration-500"
                                >
                                    {/* Specify type for browser compatibility and speed */}
                                    <source src={item.src} type="video/webm" />
                                    {/* Fallback to a static image if video is not supported */}
                                    <img src="/inosin-main-1.png" alt={item.caption} /> 
                                </video>
                            ) : (
                                // Use <img> tag for static images
                                <img 
                                    src={item.src} 
                                    alt={`Main Page Showcase ${item.id}`} 
                                    className="w-full h-auto object-cover transition-transform duration-500 hover:scale-[1.02]"
                                />
                            )}
                        </div>

                        {/* Description */}
                        <div className={`lg:col-span-5 space-y-4 ${index % 2 === 1 ? 'lg:order-1' : 'lg:order-2'}`}>
                            <span className="text-sm font-bold uppercase tracking-widest text-cyan-600">{item.title}</span>
                            <h3 className="text-3xl font-extrabold text-slate-900">
                                {item.caption.split(':')[0]}
                            </h3>
                            <p className="text-lg text-slate-700">
                                {item.caption.split(':')[1]}
                            </p>
                           
                        </div>
                    </AnimatedSection>
                ))}
            </div>
        </section>
        
        {/* --- 3. PRODUCT CATALOGUE SHOWCASE (2 Images) --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <AnimatedSection className="text-center mb-16">
                <h2 className="text-4xl font-extrabold text-slate-900">
                    The Functionality: Catalogue & Data Integrity
                </h2>
                <p className="text-lg text-slate-600 mt-2">
                    Turning a complex inventory of services into a navigable, high-speed digital catalogue.
                </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-10">
                {inosinProject.showcase.catalogueImages.map((item, index) => (
                    <AnimatedSection key={item.id}  className="space-y-6">
                        <div className="rounded-2xl overflow-hidden shadow-xl border border-white/50">
                            <img 
                                src={item.src} 
                                alt={`Catalogue Showcase ${item.id}`} 
                                className="w-full h-auto object-cover transition-transform duration-500 hover:scale-[1.02]"
                            />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">
                            {item.caption.split(':')[0]}
                        </h3>
                        <p className="text-md text-slate-700">
                            {item.caption.split(':')[1]}
                        </p>
                    </AnimatedSection>
                ))}
            </div>
        </section>

        {/* --- 4. CONTACT & USERBASE FEATURE SHOWCASE (1 Image) --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <AnimatedSection  className="grid lg:grid-cols-3 gap-12 items-center bg-white p-12 lg:p-16 rounded-3xl shadow-2xl border border-slate-100">
                <div className="lg:col-span-1 space-y-6">
                    <h2 className="text-4xl font-extrabold text-purple-600/90">
                        Lead Generation & CRM Foundation
                    </h2>
                    <p className="text-lg text-slate-700">
                        {inosinProject.showcase.featureImage.caption}
                    </p>
                    <div className="text-sm font-medium text-slate-600 pt-4">
                        <p className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-500"/> Secure Data Handling</p>
                        
                    </div>
                </div>
                <div className="lg:col-span-2 rounded-xl overflow-hidden shadow-2xl border border-slate-200">
                    <img 
                        src={inosinProject.showcase.featureImage.src} 
                        alt="Contact Form and Userbase" 
                        className="w-full h-auto object-cover"
                    />
                </div>
            </AnimatedSection>
        </section>



        
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <AnimatedSection className="text-center mb-12">
                <h2 className="text-4xl font-extrabold text-slate-900">
                    Quantifiable Impact & Success
                </h2>
                <p className="text-lg text-slate-600 mt-2">
                    Our work delivered concrete, measurable results that directly contributed to Innosin Labs' business growth and online establishment.
                </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-8">
                {inosinProject.results.points.map((result, index) => (
                    <AnimatedSection key={index}  className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 text-center space-y-3">
                        <div className="flex justify-center mb-4">
                            {result.icon}
                        </div>
                        <p className="text-xl font-semibold text-slate-800">
                            {result.text}
                        </p>
                    </AnimatedSection>
                ))}
            </div>
        </section>

        
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
        <AnimatedSection  className="text-center py-16 bg-white rounded-3xl shadow-2xl border border-slate-100 mx-auto max-w-5xl my-16 space-y-6">
              <h2 className="text-4xl font-extrabold text-slate-900">
                  Got a Vision? Let's Build It.
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Your next successful digital transformation starts with a free, no-obligation consultation with our engineering team.
              </p>
             
  
  <a
    href="/consult"
    className="group inline-flex items-center gap-2 px-8 py-4 
               bg-cyan-500 hover:bg-cyan-600 
               text-white font-bold 
               rounded-full 
               shadow-lg 
               transition-all duration-200 
               hover:scale-105 
               text-lg"
  >
    <span className="flex items-center gap-2">
      Book Your Free Consultation
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
    </span>
  </a>




          </AnimatedSection>
        </section>

        
        <FooterSection />
      </main>
    </div>
  );
}